import { useEffect, useState, } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getIsLoading,
    getModules,
    getActivities,
    getMedia,
    getActivityTypes,
    getQuestionTypes,
    getSessionTypes,
    getActivity,
} from "../../selectors";
import {
    createCourseActivityCoursesMasterAction,
    createCourseModuleCoursesMasterAction,
    deleteCourseActivityCoursesMasterAction,
    deleteCourseModuleCoursesMasterAction,
    getCourseActivitiesCoursesMasterAction,
    getCourseActivityCoursesMasterAction,
    getCourseModulesCoursesMasterAction,
    updateCourseActivityCoursesMasterAction,
    updateCourseModuleCoursesMasterAction,
} from "../../actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString } from "@icarius-localization/strings";
import { openDialogAction } from "@icarius-common/dialog/actions";
import { RESET_ACTIVITY } from "../../actionTypes";

const useCourse = (courseId) => {

    const [selectedModule, setSelectedModule] = useState(null);
    const [moduleFormIsOpen, setModuleFormIsOpen] = useState(false);
    const [activityFormIsOpen, setActivityFormIsOpen] = useState(false);
    const [activityInvalidFiles, setActivityInvalidFiles] = useState(null); // para el activityFormDialog

    const dispatch = useDispatch();
    const isLoading = useSelector(getIsLoading);
    const modules = useSelector(getModules);
    const activities = useSelector(getActivities);
    const media = useSelector(getMedia);
    const activityTypes = useSelector(getActivityTypes);
    const questionTypes = useSelector(getQuestionTypes);
    const sessionTypes = useSelector(getSessionTypes);
    const activityFormData = useSelector(getActivity);

    useEffect(() => {
        dispatch(getCourseModulesCoursesMasterAction(courseId));
        dispatch(getCourseActivitiesCoursesMasterAction(courseId));
    }, [dispatch, courseId])

    const handleOpenCreateModuleForm = () => {
        setModuleFormIsOpen(true);
    }

    const handleOpenUpdateModuleForm = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        setSelectedModule(selectedRows[0]);
        setModuleFormIsOpen(true);
    }

    const handleCloseModuleForm = () => {
        setModuleFormIsOpen(false);
        setSelectedModule(null);
    }

    const handleCreateModule = (dataToSend) => {
        dispatch(createCourseModuleCoursesMasterAction(courseId, dataToSend))
            .then((res) => res?.status === 200 && handleCloseModuleForm());
    }

    const handleUpdateModule = (dataToSend) => {
        dispatch(updateCourseModuleCoursesMasterAction(courseId, selectedModule.id, dataToSend))
            .then((resp) => {
                if (resp.status === 200) {
                    dispatch(getCourseActivitiesCoursesMasterAction(courseId))
                        .then((resp) => {
                            if (resp.status === 200) {
                                handleCloseModuleForm();
                            }
                        })
                }
            })
    }

    const handleOpenDeleteModuleDialog = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const moduleId = selectedRows[0].id;
        dispatch(openDialogAction({
            title: getLocalizedString("attention"),
            msg: getLocalizedString("doYouWantToDeleteModule"),
            onConfirm: () => dispatch(deleteCourseModuleCoursesMasterAction(courseId, moduleId)),
        }));
    }

    const handleOpenCreateActivityForm = () => {
        setActivityFormIsOpen(true);
    }

    const handleOpenUpdateActivityForm = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const activityId = selectedRows[0].id;
        dispatch(getCourseActivityCoursesMasterAction(courseId, activityId))
            .then((resp) => {
                if (resp.status === 200) {
                    setActivityFormIsOpen(true);
                }
            })
    }

    const handleCloseActivityForm = () => {
        setActivityFormIsOpen(false);
        dispatch({ type: RESET_ACTIVITY });
    }

    const handleCreateActivity = (dataToSend) => {
        dispatch(createCourseActivityCoursesMasterAction(courseId, dataToSend))
            .then((resp) => {
                if (resp?.status === 200) {
                    handleCloseActivityForm();
                } else {
                    if (resp?.data?.invalidFiles) {
                        setActivityInvalidFiles(resp.data.invalidFiles)
                    }
                }
            });
    }

    const handleUpdateActivity = (dataToSend) => {
        dispatch(updateCourseActivityCoursesMasterAction(courseId, activityFormData.id, dataToSend))
            .then((resp) => {
                if (resp?.status === 200) {
                    handleCloseActivityForm();
                } else {
                    if (resp?.data?.invalidFiles) {
                        setActivityInvalidFiles(resp.data.invalidFiles)
                    }
                }
            });
    }

    const handleOpenDeleteActivityDialog = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const activityId = selectedRows[0].id;
        dispatch(openDialogAction({
            title: getLocalizedString("attention"),
            msg: getLocalizedString("doYouWantToDeleteActivity"),
            onConfirm: () => dispatch(deleteCourseActivityCoursesMasterAction(courseId, activityId)),
        }));
    }

    const handleClearInvalidFiles = () => {
        setActivityInvalidFiles(null);
    }

    const state = {
        selectedModule,
        moduleFormIsOpen,
        activityFormIsOpen,
        activityInvalidFiles,
    }

    const handlers = {
        handleOpenCreateModuleForm,
        handleOpenUpdateModuleForm,
        handleCloseModuleForm,
        handleOpenDeleteModuleDialog,
        handleCreateModule,
        handleUpdateModule,
        handleOpenCreateActivityForm,
        handleOpenUpdateActivityForm,
        handleCloseActivityForm,
        handleOpenDeleteActivityDialog,
        handleCreateActivity,
        handleUpdateActivity,
        handleClearInvalidFiles,
    }

    return {
        modules,
        activities,
        media,
        activityTypes,
        questionTypes,
        sessionTypes,
        activityFormData,
        isLoading,
        state,
        handlers,
    }
}

export default useCourse;